import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ChevronLeft, Home, Building2, Loader2, PoundSterling, Bed, Bath, Sofa
} from "lucide-react";
import { API } from "@/App";
import { PostcodeLookup } from "@/components/PostcodeLookup";

const PROPERTY_TYPES = [
  { value: "detached", label: "Detached House" },
  { value: "semi_detached", label: "Semi-Detached House" },
  { value: "terraced", label: "Terraced House" },
  { value: "flat", label: "Flat/Apartment" },
  { value: "bungalow", label: "Bungalow" },
  { value: "maisonette", label: "Maisonette" },
  { value: "cottage", label: "Cottage" },
  { value: "townhouse", label: "Townhouse" }
];

const PRICING_TYPES = [
  { value: "fixed", label: "Fixed Price" },
  { value: "offers_over", label: "Offers Over" },
  { value: "oiro", label: "OIRO" },
  { value: "guide_price", label: "Guide Price" }
];

const FURNISHED_OPTIONS = [
  { value: "unfurnished", label: "Unfurnished" },
  { value: "part_furnished", label: "Part Furnished" },
  { value: "furnished", label: "Furnished" }
];

export const ListingNewPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = localStorage.getItem("token");
  
  const contactId = searchParams.get("contact");
  const valuationId = searchParams.get("valuation");
  const listingType = searchParams.get("type") || "sales";
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [contact, setContact] = useState(null);
  const [valuation, setValuation] = useState(null);
  const [staffList, setStaffList] = useState([]);
  
  // Form data
  const [formData, setFormData] = useState({
    // Property
    address_line1: "",
    address_line2: "",
    town: "",
    county: "",
    postcode: "",
    property_type: "semi_detached",
    bedrooms: 3,
    bathrooms: 1,
    receptions: 1,
    tenure: "freehold",
    // Pricing (Sales)
    price: "",
    pricing_type: "fixed",
    // Rent (Lettings)
    rent_pcm: "",
    deposit: "",
    available_from: "",
    minimum_term: 6,
    furnished: "unfurnished",
    bills_included: false,
    pets_allowed: false,
    smokers_allowed: false,
    students_accepted: true,
    dss_accepted: false,
    parking: false,
    garden: false,
    // Content
    summary: "",
    description: "",
    // EPC
    epc_rating: "",
    epc_expiry: "",
    // Leasehold
    lease_years_remaining: "",
    ground_rent: "",
    service_charge: "",
    // Lettings compliance
    gas_safety_expiry: "",
    eicr_expiry: "",
    // Assignment
    branch: "Head Office",
    negotiator_id: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch contact
      if (contactId) {
        const contactRes = await axios.get(`${API}/contacts/${contactId}`, { headers });
        setContact(contactRes.data?.contact);
        
        // Pre-fill address from contact
        if (contactRes.data?.contact?.address) {
          const addr = contactRes.data.contact.address;
          setFormData(prev => ({
            ...prev,
            address_line1: addr.line1 || "",
            address_line2: addr.line2 || "",
            town: addr.town || "",
            county: addr.county || "",
            postcode: addr.postcode || ""
          }));
        }
      }
      
      // Fetch valuation if provided
      if (valuationId) {
        const valRes = await axios.get(`${API}/contacts/valuations/${valuationId}`, { headers });
        setValuation(valRes.data?.valuation);
        
        // Pre-fill from valuation
        if (valRes.data?.valuation?.property) {
          const prop = valRes.data.valuation.property;
          setFormData(prev => ({
            ...prev,
            address_line1: prop.address_line1 || prev.address_line1,
            address_line2: prop.address_line2 || prev.address_line2,
            town: prop.town || prev.town,
            county: prop.county || prev.county,
            postcode: prop.postcode || prev.postcode,
            property_type: prop.property_type || prev.property_type,
            bedrooms: prop.bedrooms || prev.bedrooms,
            tenure: prop.tenure || prev.tenure
          }));
        }
        
        // Pre-fill recommended price if available
        if (valRes.data?.valuation?.recommended_price) {
          if (listingType === "sales") {
            setFormData(prev => ({ ...prev, price: valRes.data.valuation.recommended_price }));
          } else {
            setFormData(prev => ({ ...prev, rent_pcm: valRes.data.valuation.recommended_price }));
          }
        }
      }
      
      // Fetch staff for negotiator dropdown
      const staffRes = await axios.get(`${API}/staff`, { headers });
      setStaffList(staffRes.data?.staff || []);
      
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [token, contactId, valuationId, listingType]);

  useEffect(() => {
    if (!contactId) {
      toast.error("Contact ID is required");
      navigate("/contacts");
      return;
    }
    fetchData();
  }, [fetchData, contactId, navigate]);

  const handleSubmit = async () => {
    // Validation
    if (!formData.address_line1 || !formData.postcode) {
      toast.error("Please enter property address and postcode");
      return;
    }
    
    if (listingType === "sales" && !formData.price) {
      toast.error("Please enter the asking price");
      return;
    }
    
    if (listingType === "lettings" && !formData.rent_pcm) {
      toast.error("Please enter the monthly rent");
      return;
    }
    
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const endpoint = listingType === "sales" ? `${API}/listings/sales` : `${API}/listings/lettings`;
      
      const payload = {
        contact_id: contactId,
        valuation_id: valuationId || null,
        ...formData,
        price: formData.price ? parseInt(formData.price) : null,
        rent_pcm: formData.rent_pcm ? parseInt(formData.rent_pcm) : null,
        deposit: formData.deposit ? parseInt(formData.deposit) : null,
        bedrooms: parseInt(formData.bedrooms),
        bathrooms: parseInt(formData.bathrooms),
        receptions: parseInt(formData.receptions),
        minimum_term: parseInt(formData.minimum_term),
        lease_years_remaining: formData.lease_years_remaining ? parseInt(formData.lease_years_remaining) : null,
        ground_rent: formData.ground_rent ? parseInt(formData.ground_rent) : null,
        service_charge: formData.service_charge ? parseInt(formData.service_charge) : null
      };
      
      const res = await axios.post(endpoint, payload, { headers });
      
      toast.success("Listing created successfully");
      
      // Navigate to the new listing detail page
      if (res.data?.listing?.listing_id) {
        navigate(`/listings/${res.data.listing.listing_id}`);
      } else {
        navigate(`/contacts/${contactId}`);
      }
    } catch (error) {
      console.error("Error creating listing:", error);
      toast.error(error.response?.data?.detail || "Failed to create listing");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
      </div>
    );
  }

  const isSales = listingType === "sales";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-2">
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-lg ${isSales ? "bg-blue-100" : "bg-purple-100"} flex items-center justify-center`}>
              <Building2 className={`w-6 h-6 ${isSales ? "text-blue-600" : "text-purple-600"}`} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">
                New {isSales ? "Sales" : "Lettings"} Listing
              </h1>
              <p className="text-sm text-slate-500">
                {contact?.full_name && `For ${contact.full_name}`}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <ScrollArea className="h-full">
          <div className="space-y-6">
            {/* Property Address */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Home className="w-5 h-5" />
                  Property Address
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <PostcodeLookup
                  onAddressSelect={(addr) => {
                    setFormData({
                      ...formData,
                      address_line1: addr.line_1 + (addr.line_2 ? ", " + addr.line_2 : ""),
                      address_line2: addr.line_3 || "",
                      town: addr.town_or_city || "",
                      county: addr.county || "",
                      postcode: addr.postcode || ""
                    });
                  }}
                />
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Address Line 1 *</Label>
                    <Input
                      value={formData.address_line1}
                      onChange={(e) => setFormData({...formData, address_line1: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Town *</Label>
                    <Input
                      value={formData.town}
                      onChange={(e) => setFormData({...formData, town: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Postcode *</Label>
                    <Input
                      value={formData.postcode}
                      onChange={(e) => setFormData({...formData, postcode: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Property Details */}
            <Card>
              <CardHeader>
                <CardTitle>Property Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Property Type</Label>
                    <Select value={formData.property_type} onValueChange={(v) => setFormData({...formData, property_type: v})}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PROPERTY_TYPES.map(pt => (
                          <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {isSales && (
                    <div>
                      <Label>Tenure</Label>
                      <Select value={formData.tenure} onValueChange={(v) => setFormData({...formData, tenure: v})}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="freehold">Freehold</SelectItem>
                          <SelectItem value="leasehold">Leasehold</SelectItem>
                          <SelectItem value="share_of_freehold">Share of Freehold</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="flex items-center gap-1">
                      <Bed className="w-4 h-4" /> Bedrooms
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.bedrooms}
                      onChange={(e) => setFormData({...formData, bedrooms: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1">
                      <Bath className="w-4 h-4" /> Bathrooms
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.bathrooms}
                      onChange={(e) => setFormData({...formData, bathrooms: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1">
                      <Sofa className="w-4 h-4" /> Receptions
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.receptions}
                      onChange={(e) => setFormData({...formData, receptions: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pricing (Sales) */}
            {isSales && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PoundSterling className="w-5 h-5" />
                    Pricing
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Asking Price *</Label>
                      <div className="relative mt-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">£</span>
                        <Input
                          type="number"
                          value={formData.price}
                          onChange={(e) => setFormData({...formData, price: e.target.value})}
                          className="pl-7"
                          placeholder="250000"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Pricing Type</Label>
                      <Select value={formData.pricing_type} onValueChange={(v) => setFormData({...formData, pricing_type: v})}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PRICING_TYPES.map(pt => (
                            <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Leasehold fields */}
                  {formData.tenure === "leasehold" && (
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <Label className="text-amber-800 font-semibold mb-3 block">Leasehold Details</Label>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label>Years Remaining</Label>
                          <Input
                            type="number"
                            value={formData.lease_years_remaining}
                            onChange={(e) => setFormData({...formData, lease_years_remaining: e.target.value})}
                            className="mt-1 bg-white"
                          />
                        </div>
                        <div>
                          <Label>Ground Rent (pa)</Label>
                          <Input
                            type="number"
                            value={formData.ground_rent}
                            onChange={(e) => setFormData({...formData, ground_rent: e.target.value})}
                            className="mt-1 bg-white"
                            placeholder="£"
                          />
                        </div>
                        <div>
                          <Label>Service Charge (pa)</Label>
                          <Input
                            type="number"
                            value={formData.service_charge}
                            onChange={(e) => setFormData({...formData, service_charge: e.target.value})}
                            className="mt-1 bg-white"
                            placeholder="£"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Rent (Lettings) */}
            {!isSales && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PoundSterling className="w-5 h-5" />
                    Rent & Tenancy
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Monthly Rent *</Label>
                      <div className="relative mt-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">£</span>
                        <Input
                          type="number"
                          value={formData.rent_pcm}
                          onChange={(e) => setFormData({...formData, rent_pcm: e.target.value})}
                          className="pl-7"
                          placeholder="1200"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Deposit</Label>
                      <div className="relative mt-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">£</span>
                        <Input
                          type="number"
                          value={formData.deposit}
                          onChange={(e) => setFormData({...formData, deposit: e.target.value})}
                          className="pl-7"
                          placeholder="Auto-calculated"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Min Term (months)</Label>
                      <Input
                        type="number"
                        value={formData.minimum_term}
                        onChange={(e) => setFormData({...formData, minimum_term: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Available From</Label>
                      <Input
                        type="date"
                        value={formData.available_from}
                        onChange={(e) => setFormData({...formData, available_from: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Furnished</Label>
                      <Select value={formData.furnished} onValueChange={(v) => setFormData({...formData, furnished: v})}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FURNISHED_OPTIONS.map(fo => (
                            <SelectItem key={fo.value} value={fo.value}>{fo.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={formData.pets_allowed}
                        onCheckedChange={(v) => setFormData({...formData, pets_allowed: v})}
                      />
                      <Label>Pets Allowed</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={formData.parking}
                        onCheckedChange={(v) => setFormData({...formData, parking: v})}
                      />
                      <Label>Parking</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={formData.garden}
                        onCheckedChange={(v) => setFormData({...formData, garden: v})}
                      />
                      <Label>Garden</Label>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* EPC */}
            <Card>
              <CardHeader>
                <CardTitle>EPC Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>EPC Rating</Label>
                    <Select value={formData.epc_rating} onValueChange={(v) => setFormData({...formData, epc_rating: v})}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select rating" />
                      </SelectTrigger>
                      <SelectContent>
                        {["A", "B", "C", "D", "E", "F", "G"].map(r => (
                          <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>EPC Expiry</Label>
                    <Input
                      type="date"
                      value={formData.epc_expiry}
                      onChange={(e) => setFormData({...formData, epc_expiry: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Description */}
            <Card>
              <CardHeader>
                <CardTitle>Property Description</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Summary (for portal feeds)</Label>
                  <Textarea
                    value={formData.summary}
                    onChange={(e) => setFormData({...formData, summary: e.target.value})}
                    className="mt-1"
                    rows={2}
                    placeholder="Brief summary for property portals..."
                  />
                </div>
                <div>
                  <Label>Full Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="mt-1"
                    rows={6}
                    placeholder="Detailed property description..."
                  />
                </div>
              </CardContent>
            </Card>

            {/* Assignment */}
            <Card>
              <CardHeader>
                <CardTitle>Assignment</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Branch</Label>
                    <Select value={formData.branch} onValueChange={(v) => setFormData({...formData, branch: v})}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Head Office">Head Office</SelectItem>
                        <SelectItem value="Bedford Office">Bedford Office</SelectItem>
                        <SelectItem value="Kempston Office">Kempston Office</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Negotiator</Label>
                    <Select value={formData.negotiator_id} onValueChange={(v) => setFormData({...formData, negotiator_id: v})}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select negotiator" />
                      </SelectTrigger>
                      <SelectContent>
                        {staffList.map(staff => (
                          <SelectItem key={staff.user_id} value={staff.user_id}>{staff.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Submit */}
            <div className="flex justify-end gap-3 pb-8">
              <Button variant="outline" onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={saving} data-testid="create-listing-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create Listing
              </Button>
            </div>
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};

export default ListingNewPage;
